import React from 'react';
import DataExplorerToolResult from './toolResults/DataExplorerToolResult';
import NmsToolResult from './toolResults/NmsToolResult';
import WebSearchToolResult from './toolResults/WebSearchToolResult';
import WhoisToolResult from './toolResults/WhoisToolResult';
import AlertToolResult from './toolResults/AlertToolResult';
import SyntheticsToolResult from './toolResults/SyntheticsToolResult';
import SyntheticsTestResultsToolResult from './toolResults/SyntheticsTestResultsToolResult';
import DeviceInventoryToolResult from './toolResults/DeviceInventoryToolResult';
import DeviceConnectionsToolResult from './toolResults/DeviceConnectionsToolResult';
import DeviceConfigToolResult from './toolResults/DeviceConfigToolResult';
import KbToolResult from './toolResults/KbToolResult';
import SyslogToolResult from './toolResults/SyslogToolResult';
import DefaultToolResult from './toolResults/DefaultToolResult';
import InterfaceInventoryToolResult from './toolResults/InterfaceInventoryToolResult';
import SiteInventoryToolResult from './toolResults/SiteInventoryToolResult';
import CustomApplicationsToolResult from './toolResults/CustomApplicationsToolResult';
import ConnectivityTestToolResult from './toolResults/ConnectivityTestToolResult';
import AWSInventoryToolResult from './toolResults/cloud/AwsInventoryToolResult';
import AzureInventoryToolResult from './toolResults/cloud/AzureInventoryToolResult';
import AzureResourceGraphToolResult from './toolResults/cloud/AzureResourceGraphToolResult';
import DeviceCommandToolResult from './toolResults/DeviceCommandToolResult';
import RunbookToolResult from './toolResults/RunbookToolResult';

// Tool name constants
export const TOOL_NAMES = {
  DATA_EXPLORER: 'data_explorer',
  SNMP_TRAP: 'snmp_trap',
  NMS: 'nms',
  ALERTS: 'alerts_tool',
  DEVICE_INVENTORY: 'device_inventory',
  DEVICE_CONNECTIONS: 'device_connections',
  DEVICE_CONFIG: 'device_config',
  INTERFACE_INVENTORY: 'interface_inventory',
  SITE_INVENTORY: 'site_inventory',
  CUSTOM_APPLICATIONS: 'custom_applications',
  KNOWLEDGE_BASE: 'knowledge_base',
  SYNTHETICS: 'synthetics_test_status',
  SYNTHETICS_TEST_RESULTS: 'synthetics_test_results',
  CONNECTIVITY_TEST: 'connectivity-test',
  WEB_SEARCH: 'web_search',
  WHOIS: 'whois',
  A2A: 'a2a',
  SYSLOG: 'syslog',
  AWS_INVENTORY: 'aws_inventory',
  AZURE_INVENTORY: 'azure_inventory',
  AZURE_RESOURCE_GRAPH: 'azure_resource_graph_query',
  DEVICE_COMMAND: 'device_command',
  RUNBOOKS: 'runbooks'
};

/**
 * Factory function to create the appropriate tool result component
 * based on the tool type. This centralizes the logic and makes it
 * reusable across different contexts (panel, inline, etc.)
 */
export const createToolResultComponent = (tool, props = {}) => {
  const {
    onCloseToolResult,
    onDataViewCreate,
    onQueryError,
    chartHeight = 500,
    isInlineMode = false,
    ...otherProps
  } = props;

  const toolResultProps = {
    key: tool.id || tool.tool_tracking_id,
    tool,
    onCloseToolResult,
    isInlineMode,
    ...otherProps
  };

  switch (tool.tool_name) {
    case TOOL_NAMES.DATA_EXPLORER:
      return (
        <DataExplorerToolResult
          {...toolResultProps}
          onDataViewCreate={onDataViewCreate}
          onQueryError={onQueryError}
          chartHeight={chartHeight}
        />
      );
    case TOOL_NAMES.SNMP_TRAP:
      return (
        <DataExplorerToolResult
          {...toolResultProps}
          onDataViewCreate={onDataViewCreate}
          onQueryError={onQueryError}
          chartHeight={chartHeight}
        />
      );
    case TOOL_NAMES.NMS:
      return <NmsToolResult {...toolResultProps} chartHeight={chartHeight} />;
    case TOOL_NAMES.ALERTS:
      return <AlertToolResult {...toolResultProps} />;
    case TOOL_NAMES.DEVICE_INVENTORY:
      return <DeviceInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.DEVICE_CONNECTIONS:
      return <DeviceConnectionsToolResult {...toolResultProps} />;
    case TOOL_NAMES.DEVICE_CONFIG:
      return <DeviceConfigToolResult {...toolResultProps} />;
    case TOOL_NAMES.INTERFACE_INVENTORY:
      return <InterfaceInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.SITE_INVENTORY:
      return <SiteInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.CUSTOM_APPLICATIONS:
      return <CustomApplicationsToolResult {...toolResultProps} />;
    case TOOL_NAMES.KNOWLEDGE_BASE:
      return <KbToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYNTHETICS:
      return <SyntheticsToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYNTHETICS_TEST_RESULTS:
      return <SyntheticsTestResultsToolResult {...toolResultProps} />;
    case TOOL_NAMES.CONNECTIVITY_TEST:
      return <ConnectivityTestToolResult {...toolResultProps} />;
    case TOOL_NAMES.WEB_SEARCH:
      return <WebSearchToolResult {...toolResultProps} />;
    case TOOL_NAMES.WHOIS:
      return <WhoisToolResult {...toolResultProps} />;
    case TOOL_NAMES.AWS_INVENTORY:
      return <AWSInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.AZURE_INVENTORY:
      return <AzureInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.AZURE_RESOURCE_GRAPH:
      return <AzureResourceGraphToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYSLOG:
      return (
        <SyslogToolResult
          {...toolResultProps}
          onDataViewCreate={onDataViewCreate}
          onQueryError={onQueryError}
          chartHeight={chartHeight}
        />
      );
    case TOOL_NAMES.DEVICE_COMMAND:
      return <DeviceCommandToolResult {...toolResultProps} />;
    case TOOL_NAMES.RUNBOOKS:
      return <RunbookToolResult {...toolResultProps} />;
    default:
      return <DefaultToolResult {...toolResultProps} />;
  }
};

/**
 * List of tool names that have detailed result views
 */
export const hasToolResultView = [
  TOOL_NAMES.DATA_EXPLORER,
  TOOL_NAMES.NMS,
  TOOL_NAMES.KNOWLEDGE_BASE,
  TOOL_NAMES.ALERTS,
  TOOL_NAMES.SYNTHETICS,
  TOOL_NAMES.SYNTHETICS_TEST_RESULTS,
  TOOL_NAMES.CONNECTIVITY_TEST,
  TOOL_NAMES.WEB_SEARCH,
  TOOL_NAMES.WHOIS,
  TOOL_NAMES.A2A,
  TOOL_NAMES.INTERFACE_INVENTORY,
  TOOL_NAMES.DEVICE_INVENTORY,
  TOOL_NAMES.DEVICE_CONNECTIONS,
  TOOL_NAMES.DEVICE_CONFIG,
  TOOL_NAMES.SITE_INVENTORY,
  TOOL_NAMES.CUSTOM_APPLICATIONS,
  TOOL_NAMES.SNMP_TRAP,
  TOOL_NAMES.SYSLOG,
  TOOL_NAMES.AWS_INVENTORY,
  TOOL_NAMES.AZURE_INVENTORY,
  TOOL_NAMES.AZURE_RESOURCE_GRAPH,
  TOOL_NAMES.DEVICE_COMMAND,
  TOOL_NAMES.RUNBOOKS
];
